/**
 * Parse the bootstrap the host puts in the app frame's URL hash:
 *
 * ```text
 *   #v=1&k=<base64url-no-pad-key>&o=<base64url-no-pad-peer-origin>
 * ```
 *
 * The one minter is `mint_iframe_hash` in
 * `platform/crates/agentic-app-host/src/spa/url_hash.rs`, which the Desktop, Android (FFI) and
 * web-proxy hosts all call. `k` is the attach's 32-byte HMAC-SHA256 envelope key; `o` is the
 * origin the host signs as, which the page pins as `peerOrigin`. An unknown `v`, a missing field,
 * bad base64url or a key of any other length throws, and `AppBridge` reports status `error`
 * rather than signing with a key the host never minted.
 */

/** Bytes in the HMAC-SHA256 envelope key the host mints for each attach. */
const AGENTIC_APP_KEY_BYTES = 32;

/** Parsed shape of the v=1 agentic-app iframe URL hash. */
export interface ParsedAgenticAppHash {
  readonly version: 1;
  /** The 32-byte HMAC envelope key for this attach. */
  readonly key: Uint8Array;
  /** The host (parent window) origin: where the page posts, and what every inbound `event.origin` must be. */
  readonly peerOrigin: string;
}

/** Parse the frame's URL hash. `AppBridge.start()` calls this; an app never needs to. */
export function parseAgenticAppHash(hash: string): ParsedAgenticAppHash {
  const trimmed = hash.startsWith("#") ? hash.slice(1) : hash;
  const parts = new Map<string, string>();
  for (const kv of trimmed.split("&")) {
    if (kv.length === 0) continue;
    const eq = kv.indexOf("=");
    if (eq === -1) continue;
    parts.set(kv.slice(0, eq), kv.slice(eq + 1));
  }

  const v = parts.get("v");
  if (v !== "1") {
    throw new Error(
      `parseAgenticAppHash: unsupported version ${
        v === undefined ? "<missing>" : `"${v}"`
      }, expected v=1`,
    );
  }

  const k = parts.get("k");
  if (k === undefined || k.length === 0) {
    throw new Error("parseAgenticAppHash: missing k= field");
  }
  const o = parts.get("o");
  if (o === undefined || o.length === 0) {
    throw new Error("parseAgenticAppHash: missing o= field");
  }

  const key = base64urlDecode(k);
  if (key.length !== AGENTIC_APP_KEY_BYTES) {
    throw new Error(
      `parseAgenticAppHash: k= is ${key.length} bytes, expected ${AGENTIC_APP_KEY_BYTES}`,
    );
  }
  const originBytes = base64urlDecode(o);
  const peerOrigin = new TextDecoder("utf-8", { fatal: true }).decode(
    originBytes,
  );

  return { version: 1, key, peerOrigin };
}

/**
 * Decode a base64url-no-pad string to a `Uint8Array`. Browsers
 * don't have native base64url support, so we convert `-_` → `+/`
 * and re-add the padding before calling `atob`.
 */
function base64urlDecode(s: string): Uint8Array {
  // Restore base64-standard alphabet for atob.
  const standard = s.replace(/-/g, "+").replace(/_/g, "/");
  const padding = "=".repeat((4 - (standard.length % 4)) % 4);
  let binary: string;
  try {
    binary = atob(standard + padding);
  } catch (e) {
    throw new Error(
      `parseAgenticAppHash: invalid base64url payload (${
        e instanceof Error ? e.message : String(e)
      })`,
    );
  }
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}
